import { useMemo } from 'react'
import { useParams } from 'react-router-dom'

import { routes } from '@sketch/modules-common'

import { useVersioning } from '../../versioning'
import { usePreview } from '../../shares/views/PreviewView/PreviewContext'
import { CanvasHost, CanvasLinks } from './types'

type Versioning = ReturnType<typeof useVersioning>

/**
 * Who owns the document on the canvas.
 *
 * Derived rather than provided: both contexts it reads from - versioning and
 * Preview - already sit above every canvas surface, so there is nothing extra
 * to mount. It becomes a provider when a host needs to carry something those
 * contexts do not have.
 *
 * Returns undefined outside a Share or a Preview.
 */
export function useCanvasHost(): CanvasHost | undefined {
  const preview = usePreview()
  const versioning = useVersioning()
  const { previewID } = useParams<{ previewID?: string }>()

  // `useVersioning` fabricates a version when there is no share context, so a
  // Preview would otherwise look like a Share with an empty identifier.
  const isPreview = !!preview

  const shareIdentifier = versioning.loading
    ? undefined
    : versioning.share.identifier
  const { getPathname } = versioning

  return useMemo((): CanvasHost | undefined => {
    if (isPreview) {
      if (!previewID) {
        return undefined
      }

      // A Preview has no versions, so its links are already canonical.
      const links = previewLinks(previewID)
      return {
        kind: 'preview',
        identifier: previewID,
        links,
        canonicalLinks: links,
      }
    }

    if (!shareIdentifier) {
      return undefined
    }

    return {
      kind: 'share',
      identifier: shareIdentifier,
      links: shareLinks(shareIdentifier, getPathname),
      canonicalLinks: canonicalShareLinks(shareIdentifier),
    }
  }, [isPreview, previewID, shareIdentifier, getPathname])
}

function previewLinks(previewID: string): CanvasLinks {
  return {
    page: pageUUID =>
      pageUUID
        ? routes.PREVIEW_PAGE_VIEW.create({ previewID, pageUUID })
        : null,
    section: (pageUUID, sectionUUID) =>
      pageUUID
        ? routes.PREVIEW_PAGE_SECTION.create({
            previewID,
            pageUUID,
            sectionUUID,
          })
        : null,
    frame: (pageUUID, frameUUID) =>
      pageUUID
        ? routes.PREVIEW_FRAME_VIEW.create({ previewID, pageUUID, frameUUID })
        : null,
  }
}

function shareLinks(
  shareID: string,
  getPathname: Versioning['getPathname']
): CanvasLinks {
  return {
    page: pageUUID =>
      pageUUID
        ? getPathname({
            routeKey: 'SHARE_PAGE_CANVAS_VIEW',
            routeParams: { shareID, pageUUID },
          })
        : null,
    section: (pageUUID, sectionUUID) =>
      pageUUID
        ? getPathname({
            routeKey: 'PAGE_SECTION',
            routeParams: { shareID, pageUUID, sectionUUID },
          })
        : null,
    // The Share frame route identifies the frame on its own - no page in it.
    frame: (_pageUUID, frameUUID) =>
      getPathname({
        routeKey: 'FRAME',
        routeParams: { shareID, frameUUID },
      }),
  }
}

function canonicalShareLinks(shareID: string): CanvasLinks {
  return {
    page: pageUUID =>
      pageUUID
        ? routes.SHARE_PAGE_CANVAS_VIEW.create({ shareID, pageUUID })
        : null,
    section: (pageUUID, sectionUUID) =>
      pageUUID
        ? routes.PAGE_SECTION.create({ shareID, pageUUID, sectionUUID })
        : null,
    frame: (_pageUUID, frameUUID) =>
      routes.FRAME.create({ shareID, frameUUID }),
  }
}
